import {
  JWT_COOKIE,
  clearJwtCookies,
  setJwtCookies,
  storedJwtUsableForShop,
} from "@/lib/jwt-shop-scope";
import { decodeJWT } from "@/lib/utils";
import Cookies from "js-cookie";
import { useRouter, useSearchParams } from "next/navigation";
import { useMemo, useEffect } from "react";

// expectedShop is the fluid_shop (company subdomain) the surrounding page is
// scoped to — for checkout pages, the cart's company subdomain. When given,
// a stored JWT bound to a different shop is treated as logged out and the
// stale cookies are cleared. Callers without a shop context (management,
// order pages) omit it and keep the previous behavior.
function useJwt(expectedShop?: string | null) {
  const searchParams = useSearchParams();
  const router = useRouter();

  // Handle JWT from URL parameter side effect. Backend-built checkout links
  // carry fluid_shop alongside jwt (Commerce::Cart#checkout_query_params), and
  // the in-app login redirects include it too, so the binding is written with
  // the token. A jwt arriving without fluid_shop is stored unbound and will be
  // treated as stale by any shop-scoped reader.
  useEffect(() => {
    const rawJwt = searchParams?.get("jwt");

    if (rawJwt && decodeJWT(rawJwt)) {
      setJwtCookies(rawJwt, searchParams?.get("fluid_shop"));
    }

    if (rawJwt) {
      const newSearchParams = new URLSearchParams(searchParams?.toString());
      newSearchParams.delete("jwt");
      const queryString = newSearchParams.toString();
      router.replace(
        `${window.location.pathname}${queryString ? `?${queryString}` : ""}`,
      );
    }
  }, [searchParams, router]);

  // Derive JWT values from cookies/URL. A jwt in the URL is always honored —
  // it arrives together with its fluid_shop param on entry links and the
  // effect above binds it; only cookie reads are gated by the shop binding.
  const { jwt, decodedJwt } = useMemo(() => {
    const rawJwt = searchParams?.get("jwt");
    const cookieJwt = !rawJwt ? Cookies.get(JWT_COOKIE) : null;
    const usableCookieJwt =
      cookieJwt && storedJwtUsableForShop(expectedShop) ? cookieJwt : null;

    const finalJwt = rawJwt || usableCookieJwt;
    const decoded = finalJwt ? decodeJWT(finalJwt) : null;

    if (finalJwt && !decoded) {
      return { jwt: null, decodedJwt: null };
    }

    return { jwt: finalJwt, decodedJwt: decoded };
  }, [searchParams, expectedShop]);

  // Self-heal: a cookie bound to another shop is stale for this checkout.
  // Clear it and reload so every consumer re-reads the logged-out state —
  // cookie reads are not reactive. Mirrors the 403 logout in useSyncCustomer.
  // The reload cannot loop: the cookies are removed before reloading, so the
  // next run finds no jwt cookie and returns early.
  useEffect(() => {
    if (!expectedShop) return;
    if (!Cookies.get(JWT_COOKIE)) return;
    if (storedJwtUsableForShop(expectedShop)) return;

    clearJwtCookies();
    window.location.reload();
  }, [expectedShop]);

  // Clean up invalid/expired JWT cookie
  useEffect(() => {
    const cookieJwt = Cookies.get(JWT_COOKIE);
    if (cookieJwt && !decodeJWT(cookieJwt)) {
      clearJwtCookies();
    }
  }, []);

  return { jwt, decodedJwt };
}

export default useJwt;
